// Pääkartta: pohjakartta ja pelikerrokset.
//
// Kerrokset alhaalta ylös (suunnitelman kohta 5.2):
//   pohjakartta -> suuret ruudut (ruskea raja) -> lämpökartta -> omat pikkuruudut
//   (keltainen) -> perheen ruudut (pelaajan väri) -> maksimiklusteri ja -neliö -> mahdottomat ruudut (ristiruutu)
//   -> odottavat ruudut (katkoviiva) -> valmiit reitit (magenta katkoviiva)
//   -> oma retkijälki -> valmiit kohteet (rastiympyrä) -> oma sijainti
//
// Leaflet-olio luodaan kerran. Kerrokset päivitetään omissa efekteissään,
// jotta esimerkiksi uusi GPS-piste ei piirrä kaikkia ruutuja uudelleen.

import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useRef } from "preact/hooks";

import { createBaseLayer } from "../lib/basemap.js";
import { tileBounds, tileKey } from "../lib/geo.js";
import { HeatLayer } from "../lib/heatLayer.js";
import { visiblePois } from "../lib/poiLayer.js";
import { visibleRoutes } from "../lib/routeLayer.js";
import { shapePrimitives } from "../lib/shapeLayers.js";

const LAPPEENRANTA = [61.058, 28.186];
// Ruutujen täytön peittävyys: pieni, jotta karttapohja erottuu ruutujen läpi.
const TILE_FILL = 0.16;
const FAMILY_FILL = 0.2;
const FAMILY_FILL_SHARED = 0.3; // usean keräämä ruutu on hieman tummempi, ja sillä on paksumpi reuna
const SMALL_ZOOM = 17; // sama kuin palvelimen pikkuruutu (backend/app/config.py)

/** Lukee teeman värin CSS-muuttujasta, jotta kartta noudattaa tummaa teemaa. */
const cssVar = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

function rastiIcon(done, chosen = false) {
  return L.divIcon({
    className: "",
    html: `<svg class="rasti-icon ${done ? "done" : ""} ${chosen ? "chosen" : ""}" viewBox="0 0 28 28" aria-hidden="true"><circle class="ring" cx="14" cy="14" r="10"/><circle class="dot" cx="14" cy="14" r="2"/></svg>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

/** Löydetty salainen kohde: kultainen tähti. */
function secretIcon() {
  return L.divIcon({
    className: "",
    html: '<svg class="secret-icon" viewBox="0 0 28 28" aria-hidden="true"><path d="M14 3l3.1 6.6 7.2.9-5.3 5 1.4 7.1L14 19.1 7.6 22.6 9 15.5 3.7 10.5l7.2-.9z"/></svg>',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

/** Vihjerengas: kysymysmerkki renkaan keskellä. Keskipiste EI ole kohteen paikka. */
function hintIcon() {
  return L.divIcon({ className: "", html: '<div class="hint-mark" aria-hidden="true">?</div>', iconSize: [30, 30], iconAnchor: [15, 15] });
}

/** Seikkailun rasti: numeroitu ympyrä, leimattuna täytetty ja rastilla. */
function stopIcon(n, done) {
  return L.divIcon({
    className: "",
    html: `<div class="adv-stop ${done ? "done" : ""}"><span>${done ? "✓" : n}</span></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

/**
 * tiles:     näytettävät ruudut (suodatettu tai aikakoneen näkymä)
 * collected: kaikki kerätyt ruudut; "odottaa tallennusta" -päättely tarvitsee ne,
 *            vaikka kartalla näkyisi vain suodatettu osa
 * shapes:    maksimineliö, klusteri ja seuraava tavoite (/api/tiles/shapes)
 * overlays:  mitkä lisäkerrokset ovat päällä { pois, routes, shapes, next, heat, impossible }
 * heat:      lämpökartan solut nykyiseltä alueelta
 * family:    perheen yhteiskartta (lib/familyLayer.js) tai null, kun kerros ei ole päällä
 */
export function MapView({ basemap = "osm", tiles, collected, shapes, overlays, heat, family, adventure, content, hintRings, active, me, follow, theme, draft, selectedRoute, selectedPoi, onSelectPoi, onSelectRoute, onSelectHidden, onMapClick, mapRef }) {
  const el = useRef(null);
  const map = useRef(null);
  const layers = useRef({});

  // --- Luonti kerran ------------------------------------------------------
  useEffect(() => {
    const m = L.map(el.current, { zoomControl: false, attributionControl: true, preferCanvas: true }).setView(LAPPEENRANTA, 13);
    m.attributionControl.setPrefix('<a href="https://leafletjs.com">Leaflet</a>');

    const pane = (name, z) => {
      m.createPane(name);
      m.getPane(name).style.zIndex = z;
      return L.canvas({ pane: name });
    };
    const renderers = {
      big: pane("big", 405),
      small: pane("small", 410),
      family: pane("family", 411), // omien keltaisten ruutujen päällä, jotta pelaajan väri erottuu
      shape: pane("shape", 412),
      blocked: pane("blocked", 415),
      routes: pane("routes", 420),
      track: pane("track", 430),
      adventure: pane("adventure", 435),
      draft: pane("draft", 640),
    };
    // Lämpökartta piirtää oman canvasinsa isojen ja pikkuruutujen väliin.
    m.createPane("heat");
    m.getPane("heat").style.zIndex = 407;
    layers.current = {
      big: L.layerGroup().addTo(m),
      heat: new HeatLayer({ pane: "heat" }).addTo(m),
      family: L.layerGroup().addTo(m),
      small: L.layerGroup().addTo(m),
      shape: L.layerGroup().addTo(m),
      blocked: L.layerGroup().addTo(m),
      pending: L.layerGroup().addTo(m),
      routes: L.layerGroup().addTo(m),
      track: L.layerGroup().addTo(m),
      adventure: L.layerGroup().addTo(m),
      pois: L.layerGroup().addTo(m),
      hints: L.layerGroup().addTo(m),
      me: L.layerGroup().addTo(m),
      draft: L.layerGroup().addTo(m),
      grid: L.layerGroup().addTo(m),
      renderers,
    };
    map.current = m;
    if (mapRef) mapRef.current = m;
    return () => m.remove();
  }, []);

  // Pohjakartan vaihto: vanha taso pois, uusi tilalle. Pelikerrokset säilyvät.
  const baseLayer = useRef(null);
  useEffect(() => {
    const m = map.current;
    baseLayer.current?.remove();
    baseLayer.current = basemap ? createBaseLayer(basemap).addTo(m) : null;
  }, [basemap]);

  // Kartan napautus (simulaattori ja työkalut). Käsittelijä rekisteröidään
  // kerran, ja se kutsuu aina viimeisintä onMapClick-funktiota ref:n kautta.
  // Näin työkalun vaihtuessa ei ole hetkeä, jolloin napautus menisi vanhalle
  // käsittelijälle (efekti ajetaan vasta piirron jälkeen).
  const clickRef = useRef(onMapClick);
  clickRef.current = onMapClick;
  useEffect(() => {
    const m = map.current;
    const handler = (e) => clickRef.current?.(e.latlng.lat, e.latlng.lng);
    m.on("click", handler);
    return () => m.off("click", handler);
  }, []);

  // --- Kerätyt ruudut -----------------------------------------------------
  useEffect(() => {
    const { big, small, renderers } = layers.current;
    big.clearLayers();
    small.clearLayers();
    if (!tiles) return;
    const contour = cssVar("--contour");
    // Kerätyt ruudut ovat neutraali, läpikuultava peite: karttapohjan tiet, vedet ja nimet näkyvät
    // niiden läpi. Ruudun rajaus (ohut viiva) kertoo ruudun paikan, ei täyttö.
    const ink = cssVar("--ink");
    for (const t of tiles.large) {
      L.rectangle(tileBounds(t.key), { renderer: renderers.big, color: contour, weight: 2, fill: false, interactive: false }).addTo(big);
    }
    for (const t of tiles.small) {
      L.rectangle(tileBounds(t.key), {
        renderer: renderers.small,
        color: ink,
        weight: 1,
        opacity: 0.45,
        dashArray: t.manual ? "3 3" : null,
        fillColor: ink,
        fillOpacity: TILE_FILL,
        interactive: false,
      }).addTo(small);
    }
  }, [tiles, theme]);

  // --- Lämpökartta ------------------------------------------------------------
  useEffect(() => {
    layers.current.heat.setData(overlays?.heat ? heat : null);
  }, [heat, overlays?.heat]);

  // --- Perheen yhteiskartta -----------------------------------------------------
  // Yhden pelaajan ruutu saa pelaajan värin. Usean keräämä ruutu piirretään
  // tummalla, paksummalla reunalla, jotta yhteiset ruudut erottuvat värien seasta.
  useEffect(() => {
    const { family: layer, renderers } = layers.current;
    layer.clearLayers();
    if (!family) return;
    const ink = cssVar("--ink");
    for (const t of family.tiles) {
      const color = t.color ?? ink;
      L.rectangle(tileBounds(t.key), {
        renderer: renderers.family,
        color,
        weight: t.shared ? 2.5 : 1,
        opacity: t.shared ? 0.85 : 0.6,
        fillColor: color,
        fillOpacity: t.shared ? FAMILY_FILL_SHARED : FAMILY_FILL,
        interactive: false,
      }).addTo(layer);
    }
  }, [family, theme]);

  // --- Ruutujen muodot: maksimiklusteri, maksimineliö ja seuraavan neliön puuttuvat ruudut -----
  // Mitä piirretään, päättää lib/shapeLayers.js; tässä vain tyylit. Pikkuruudut: järvensininen ja rasti.
  // Suuret ruudut: maastonruskea, paksummilla viivoilla, jotta ne erottaa pienistä.
  useEffect(() => {
    const { shape, renderers } = layers.current;
    shape.clearLayers();
    const lake = cssVar("--lake");
    const rasti = cssVar("--rasti");
    const contour = cssVar("--contour");
    const styles = {
      small: {
        cluster: { color: lake, weight: 2, fillColor: lake, fillOpacity: 0.12 },
        square: { color: rasti, weight: 4, fill: false },
        missing: { color: rasti, weight: 1.5, dashArray: "4 4", fillColor: rasti, fillOpacity: 0.14 },
        target: { color: rasti, weight: 2.5, dashArray: "10 6", fill: false },
      },
      large: {
        cluster: { color: contour, weight: 3, fillColor: contour, fillOpacity: 0.1 },
        square: { color: contour, weight: 6, fill: false },
        missing: { color: contour, weight: 2, dashArray: "6 6", fillColor: contour, fillOpacity: 0.08 },
        target: { color: contour, weight: 3, dashArray: "14 8", fill: false },
      },
    };
    for (const p of shapePrimitives(shapes, overlays)) {
      L.rectangle(p.bounds, { renderer: renderers.shape, interactive: false, ...styles[p.level][p.part] }).addTo(shape);
    }
  }, [shapes, overlays?.shapes, overlays?.next, overlays?.shapesLarge, theme]);

  // --- Mahdottomat ruudut: harmaa ruutu ja rasti ----------------------------
  useEffect(() => {
    const { blocked, renderers } = layers.current;
    blocked.clearLayers();
    if (!overlays?.impossible || !tiles?.impossible) return;
    const ink = cssVar("--ink-soft");
    for (const entry of tiles.impossible) {
      const bounds = tileBounds(entry.tile);
      const big = entry.kind === "bigtile";
      const weight = big ? 3 : 1.5;
      const style = { renderer: renderers.blocked, color: ink, weight, interactive: false };
      L.rectangle(bounds, { ...style, dashArray: big ? "10 6" : null, fillColor: ink, fillOpacity: big ? 0.12 : 0.3 }).addTo(blocked);
      const [[s, w], [n, e]] = bounds;
      L.polyline([[s, w], [n, e]], style).addTo(blocked);
      L.polyline([[s, e], [n, w]], style).addTo(blocked);
    }
  }, [tiles, overlays?.impossible, theme]);

  // --- Retken aikana: odottavat ruudut ja jälki ---------------------------
  useEffect(() => {
    const { pending, track, renderers } = layers.current;
    pending.clearLayers();
    track.clearLayers();
    if (!active) return;
    const confirmed = new Set((collected ?? tiles)?.small.map((t) => t.key));
    const openInk = cssVar("--open-ink");
    for (const key of active.localTiles) {
      if (confirmed.has(key)) continue;
      L.rectangle(tileBounds(key), { renderer: renderers.small, color: openInk, weight: 1.5, dashArray: "5 4", fill: false, interactive: false }).addTo(pending);
    }
    if (active.track.length > 1) {
      L.polyline(active.track, { renderer: renderers.track, color: cssVar("--ink"), weight: 4, opacity: 0.85, interactive: false }).addTo(track);
    }
  }, [active?.track.length, active?.localTiles.length, tiles, collected, theme]);

  // --- Valmiit kohteet ja reitit -----------------------------------------
  useEffect(() => {
    const { pois, routes, renderers } = layers.current;
    pois.clearLayers();
    routes.clearLayers();
    if (!content) return;
    const rasti = cssVar("--rasti");
    // Reitit ovat oma karttatasonsa: valintaruudulla piilotettavissa (oletuksena näkyvät).
    // Valittu reitti (Kohteet-listasta) piirretään viimeisenä, paksumpana ja yhtenäisenä, jotta se erottuu muista.
    const picked = selectedRoute ?? null;
    const drawn = visibleRoutes(content, overlays, picked).sort((a, b) => (a.id === picked) - (b.id === picked));
    for (const r of drawn) {
      const chosen = r.id === picked;
      // Vaalea reunus erottaa reitin maastokartan korkeuskäyristä.
      L.polyline(r.geometry, { renderer: renderers.routes, color: cssVar("--surface"), weight: chosen ? 13 : 9, opacity: 0.9, interactive: false }).addTo(routes);
      L.polyline(r.geometry, { renderer: renderers.routes, color: chosen ? cssVar("--lake") : rasti, weight: chosen ? 7 : 5, dashArray: chosen ? null : "12 7", opacity: 1 })
        .on("click", () => onSelectRoute?.(r.id))
        .addTo(routes);
    }
    // Kohteet ovat oma karttatasonsa; Kohteet-listasta valittu kohde näkyy aina, ja sen tunnistusalue piirretään,
    // jotta näkee, mihin pitää mennä.
    for (const p of visiblePois(content, overlays, selectedPoi ?? null)) {
      const chosen = p.id === selectedPoi;
      if (chosen) {
        L.circle([p.lat, p.lon], { renderer: renderers.routes, radius: p.visit_radius_m || 40, color: cssVar("--lake"), weight: 3, fillColor: cssVar("--lake"), fillOpacity: 0.15, interactive: false }).addTo(pois);
      }
      L.marker([p.lat, p.lon], { icon: rastiIcon(p.state.done, chosen), title: p.name, keyboard: true, zIndexOffset: chosen ? 1000 : 0 })
        .on("click", () => onSelectPoi?.(p.id))
        .addTo(pois);
    }
    // Löydetyt salaiset kohteet näkyvät vasta löydön jälkeen.
    for (const h of content.hidden ?? []) {
      L.marker([h.lat, h.lon], { icon: secretIcon(), title: h.name, keyboard: true })
        .on("click", () => onSelectHidden?.(h.id))
        .addTo(pois);
    }
  }, [content, theme, overlays?.routes, overlays?.pois, selectedRoute, selectedPoi]);

  // --- Salaisten kohteiden vihjeet: katkoviivarengas, joka sisältää kohteen mutta ei paljasta sitä -----
  useEffect(() => {
    const { hints, renderers } = layers.current;
    hints.clearLayers();
    const gold = cssVar("--gold");
    for (const r of hintRings ?? []) {
      L.circle([r.lat, r.lon], { renderer: renderers.draft, radius: r.radius_m, color: gold, weight: 3, dashArray: "4 8", fillColor: gold, fillOpacity: 0.1, interactive: false }).addTo(hints);
      L.marker([r.lat, r.lon], { icon: hintIcon(), interactive: false, keyboard: false }).addTo(hints);
    }
  }, [hintRings, theme]);

  // --- Valittu seikkailu: reitti, rastialueet ja rastit ---------------------------
  // Ruutu-rastista näytetään koko ruutu ja kohde-rastista sen tunnistusalue, jotta
  // näkee, mihin pitää mennä. Reitti on vain ehdotus: rastit saa leimata missä järjestyksessä tahansa.
  useEffect(() => {
    const { adventure: layer, renderers } = layers.current;
    layer.clearLayers();
    if (!adventure) return;
    const { card, reached } = adventure;
    const lake = cssVar("--lake");
    const rasti = cssVar("--rasti");
    // Vaalea reunus erottaa reitin maastokartan korkeuskäyristä.
    L.polyline(card.route, { renderer: renderers.adventure, color: cssVar("--surface"), weight: 8, opacity: 0.85, interactive: false }).addTo(layer);
    L.polyline(card.route, { renderer: renderers.adventure, color: lake, weight: 4, dashArray: "2 9", lineCap: "round", interactive: false }).addTo(layer);
    card.stops.forEach((stop, i) => {
      const done = reached.includes(i);
      const area = { renderer: renderers.adventure, color: rasti, weight: done ? 1 : 2, dashArray: "5 4", fillColor: rasti, fillOpacity: done ? 0.04 : 0.14, interactive: false };
      if (stop.kind === "tile") L.rectangle(tileBounds(stop.tile), area).addTo(layer);
      else L.circle([stop.lat, stop.lon], { ...area, radius: stop.radius_m ?? 40 }).addTo(layer);
      L.marker([stop.lat, stop.lon], {
        icon: stopIcon(i + 1, done),
        title: `Rasti ${i + 1}: ${stop.label}${done ? " (leimattu)" : ""}`,
        keyboard: false,
        interactive: false,
      }).addTo(layer);
    });
  }, [adventure?.id, adventure?.reached.length, theme]);

  // --- Työkalujen luonnos -----------------------------------------------------
  // circle: tunnistusalue tähtäimen ympärillä, seuraa kartan keskipistettä
  // line:   piirrettävä reittilinja pisteineen
  // tile:   valittu ruutu ja pikkuruutujen ruudukko valinnan avuksi
  useEffect(() => {
    const m = map.current;
    const { draft: layer, grid, renderers } = layers.current;
    layer.clearLayers();
    grid.clearLayers();
    if (!draft) return undefined;
    const rasti = cssVar("--rasti");
    const cleanups = [];

    if (draft.kind === "circle") {
      const circle = L.circle(m.getCenter(), { renderer: renderers.draft, radius: draft.radius, color: rasti, weight: 2, dashArray: "6 5", fillOpacity: 0.08, interactive: false }).addTo(layer);
      const follow = () => circle.setLatLng(m.getCenter());
      m.on("move", follow);
      cleanups.push(() => m.off("move", follow));
    }
    if (draft.kind === "line") {
      for (const part of draft.otherParts ?? []) {
        if (part.length > 1) L.polyline(part, { renderer: renderers.draft, color: rasti, weight: 3, opacity: 0.5, interactive: false }).addTo(layer);
      }
    }
    if (draft.kind === "line" && draft.coords.length) {
      L.polyline(draft.coords, { renderer: renderers.draft, color: rasti, weight: 4, interactive: false }).addTo(layer);
      draft.coords.forEach((p, i) =>
        L.circleMarker(p, { renderer: renderers.draft, radius: i === draft.coords.length - 1 ? 7 : 5, color: rasti, weight: 2, fillColor: cssVar("--surface"), fillOpacity: 1, interactive: false }).addTo(layer),
      );
    }
    if (draft.kind === "tile") {
      const drawGrid = () => {
        grid.clearLayers();
        // Pienen ruudun ruudukko tiheä, joten se näytetään vasta lähempää.
        if (m.getZoom() < (draft.zoom === 14 ? 10 : 15)) return;
        const b = m.getBounds();
        const z = draft.zoom ?? SMALL_ZOOM;
        const [, x0, y0] = tileKey(b.getNorth(), b.getWest(), z).split("/").map(Number);
        const [, x1, y1] = tileKey(b.getSouth(), b.getEast(), z).split("/").map(Number);
        const ink = cssVar("--ink-soft");
        for (let x = x0; x <= x1; x++) {
          for (let y = y0; y <= y1; y++) {
            L.rectangle(tileBounds(`${z}/${x}/${y}`), { renderer: renderers.draft, color: ink, weight: 0.6, opacity: 0.6, fill: false, interactive: false }).addTo(grid);
          }
        }
      };
      drawGrid();
      m.on("moveend", drawGrid);
      cleanups.push(() => m.off("moveend", drawGrid));
      if (draft.key) {
        L.rectangle(tileBounds(draft.key), { renderer: renderers.draft, color: rasti, weight: 3.5, fillColor: rasti, fillOpacity: 0.15, interactive: false }).addTo(layer);
      }
    }
    return () => cleanups.forEach((fn) => fn());
  }, [draft, theme]);

  // --- Oma sijainti ---------------------------------------------------------
  useEffect(() => {
    const { me: layer } = layers.current;
    layer.clearLayers();
    if (!me) return;
    L.marker([me.latitude, me.longitude], {
      icon: L.divIcon({ className: "", html: '<div class="me-dot"></div>', iconSize: [18, 18], iconAnchor: [9, 9] }),
      interactive: false,
      keyboard: false,
    }).addTo(layer);
    if (me.accuracy && me.accuracy < 200) {
      L.circle([me.latitude, me.longitude], { radius: me.accuracy, color: cssVar("--lake"), weight: 1, fillOpacity: 0.08, interactive: false }).addTo(layer);
    }
    if (follow) map.current.panTo([me.latitude, me.longitude], { animate: true });
  }, [me, follow]);

  return <div ref={el} class="map" role="application" aria-label="Kartta, jolla omat ruudut ja valmiit kohteet" />;
}

